import React from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import { useLocation } from "react-router-dom";

const PAGE_PURPOSES = {
  today: {
    title: "Today",
    purpose: "Today is where you act.",
    useFor: [
      "Start here, check things off, and keep the day moving.",
      "Use Reset when the full version does not happen.",
      "See what is waiting and what is done.",
    ],
    nextStep: "Start with the habit at the top.",
  },
  sprint: {
    title: "Accountability",
    purpose: "Accountability is where your Daily Report agreements live.",
    useFor: [
      "See whether you and your TrustBud submitted today’s report.",
      "Submit Done or Reset.",
      "Use the log so silence is visible instead of invisible.",
    ],
    nextStep: "Submit today’s report before the deadline.",
  },
  network: {
    title: "Activity",
    purpose: "Activity shows what is happening with your people.",
    useFor: [
      "See who is active.",
      "See who could use a check-in.",
      "Find where you can show up.",
    ],
    nextStep: "Use this when you want to know what needs attention right now.",
  },
  connections: {
    title: "Connections",
    purpose: "Connections is where your TrustBuds live.",
    useFor: [
      "Add people who can help you stay accountable.",
      "Accept, decline, or cancel requests.",
      "Start Accountability with a TrustBud connection.",
    ],
    nextStep: "Use this when you need a real person involved.",
  },
  plan: {
    title: "Plan",
    purpose: "Plan is where you shape your goals, habits, schedule, and planned times.",
    useFor: [
      "Edit your vision and goals.",
      "Create habits with daily, weekly, or monthly timing.",
      "Check whether your plan still matches what you want.",
    ],
    nextStep: "Use this when the plan itself needs adjusting.",
  },
  intelligence: {
    title: "Insights",
    purpose: "Insights is where TrustBud helps you understand yourself.",
    useFor: [
      "See what is working.",
      "See where you fall off.",
      "Learn what to change next.",
    ],
    nextStep: "Use this when you want a clearer read on your goals, habits, timing, and follow-through.",
  },
  weeklySummary: {
    title: "Weekly Summary",
    purpose: "A reflection surface for understanding what changed this week.",
    useFor: [
      "Compare this week against where you were at the same point last week.",
      "See which habits and check-ins created momentum.",
      "Decide what to carry into the next week.",
    ],
    nextStep: "Use this after a few days of activity or when planning the next week.",
  },
  person: {
    title: "Conversation",
    purpose: "This is where you talk with one TrustBud in context.",
    useFor: [
      "Reply to a TrustBud.",
      "Send encouragement or a quick reset note.",
      "Open the relevant Accountability or person context.",
    ],
    nextStep: "Use this when the relationship needs a direct message.",
  },
};

function resolvePagePurpose(pathname) {
  if (pathname.includes("/weekly-summary")) return PAGE_PURPOSES.weeklySummary;
  if (pathname.includes("/network/person") || pathname.includes("/updates/person")) {
    return PAGE_PURPOSES.person;
  }
  if (pathname.includes("/sprint") || pathname.includes("/challenge") || pathname.includes("/trustbud")) return PAGE_PURPOSES.sprint;
  if (pathname.includes("/activity") || pathname.includes("/updates") || pathname.includes("/moments")) return PAGE_PURPOSES.network;
  if (pathname.includes("/network") || pathname.includes("/connections") || pathname.includes("/companions")) return PAGE_PURPOSES.connections;
  if (pathname.includes("/intelligence")) return PAGE_PURPOSES.intelligence;
  if (pathname.includes("/plan") || pathname.includes("/manage-vision")) return PAGE_PURPOSES.plan;
  return PAGE_PURPOSES.today;
}

export default function PagePurposeHelp() {
  const location = useLocation();
  const [open, setOpen] = React.useState(false);
  const page = React.useMemo(
    () => resolvePagePurpose(location.pathname || ""),
    [location.pathname]
  );

  return (
    <>
      <IconButton
        aria-label={`What is ${page.title} for?`}
        onClick={() => setOpen(true)}
        size="small"
        sx={{
          position: "fixed",
          right: { xs: 12, lg: 24 },
          top: { xs: 76, lg: "auto" },
          bottom: { xs: "auto", lg: 24 },
          zIndex: (theme) => theme.zIndex.appBar + 1,
          width: 34,
          height: 34,
          bgcolor: "rgba(255,255,255,0.86)",
          border: "1px solid rgba(15,23,42,0.08)",
          boxShadow: "0 10px 22px rgba(15,23,42,0.10)",
          backdropFilter: "blur(14px)",
          color: "#52647b",
          "&:hover": {
            bgcolor: "#ffffff",
            color: "#17345d",
          },
        }}
      >
        <HelpOutlineIcon fontSize="small" />
      </IconButton>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle sx={{ pr: 6, pb: 1 }}>
          <Typography
            variant="caption"
            sx={{ display: "block", fontWeight: 800, letterSpacing: 0, color: "#64748b", textTransform: "uppercase" }}
          >
            Page guide
          </Typography>
          <Typography variant="h5" sx={{ mt: 0.3, fontWeight: 850, letterSpacing: 0, color: "#0f172a" }}>
            {page.title}
          </Typography>
          <IconButton
            aria-label="Close page guide"
            onClick={() => setOpen(false)}
            size="small"
            sx={{ position: "absolute", right: 14, top: 14 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 1, pb: 3 }}>
          <Typography sx={{ fontSize: 16, lineHeight: 1.5, fontWeight: 650, color: "#1e293b" }}>
            {page.purpose}
          </Typography>

          <Box sx={{ height: 1, bgcolor: "#e2e8f0", my: 2.2 }} />

          <Typography
            variant="caption"
            sx={{ fontWeight: 850, letterSpacing: 0, color: "#64748b", textTransform: "uppercase" }}
          >
            Use this page to
          </Typography>
          <Stack spacing={1.15} sx={{ mt: 1.35 }}>
            {page.useFor.map((item) => (
              <Stack key={item} direction="row" spacing={1.2} alignItems="flex-start">
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    mt: "7px",
                    borderRadius: "50%",
                    bgcolor: "#17345d",
                    flex: "0 0 auto",
                  }}
                />
                <Typography variant="body2" sx={{ color: "#334155", lineHeight: 1.55 }}>
                  {item}
                </Typography>
              </Stack>
            ))}
          </Stack>

          <Box sx={{ mt: 2.3, borderRadius: "8px", bgcolor: "#f8fafc", p: 1.7 }}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 850, letterSpacing: 0, color: "#64748b", textTransform: "uppercase" }}
            >
              Best next move
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.55, color: "#17345d", fontWeight: 700, lineHeight: 1.5 }}>
              {page.nextStep}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
